import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import AutomotiveShowcase from '@/pages/home/components/AutomotiveShowcase';

interface Project {
  id: number;
  category: string;
  featured?: boolean;
  title: string;
  location: string;
  value: string;
  description: string;
  tags: string[];
  creditRef?: string;
  parcRef?: string;
  prazoRef?: string;
  condominioRef?: string;
  iptuRef?: string;
  details: {
    area?: string;
    vehicleType?: string;
    type: string;
    completion?: string;
    bedrooms?: string;
    bathrooms?: string;
    parking?: string;
  };
}

const IMAGE_MAP: Record<number, string> = {
  1: 'https://public.readdy.ai/ai/img_res/a2e7605ad2998df0fb1c058fa094c0a4.jpg',
  2: 'https://readdy.ai/api/search-image?query=premium%20commercial%20office%20suite%20interior%20in%20Alphaville%20Barueri%20Sao%20Paulo%2C%20modern%20corporate%20workspace%2C%20glass%20partitions%2C%20executive%20meeting%20room%2C%20sophisticated%20business%20environment%2C%20neutral%20tones%2C%20high%20end%20finishes%2C%20professional%20lighting&width=800&height=520&seq=portfolio2v2&orientation=landscape',
  3: 'https://static.readdy.ai/image/eead7521a07a7e44430d3022e12ebfe4/662b0ed40394357a6a12579750f4fdfd.webp',
  4: 'https://static.readdy.ai/image/eead7521a07a7e44430d3022e12ebfe4/65feea9779739b1653da5e3cf239c4f4.webp',
  5: 'https://public.readdy.ai/ai/img_res/edited_03e459b92a728251396c6182714bee02_39eb3de5.jpg',
  6: 'https://storage.readdy-site.link/project_files/cde8128b-6de1-46ae-a588-692dddd7b4aa/fbb5b632-8f39-4f48-b49a-3971bd88eea6_bmw-320i-msport-2026.jpeg?v=a12849a1953d4ab08db6353ad7517329',
  10: 'https://readdy.ai/api/search-image?query=luxury%20gated%20community%20house%20Tambore%20Santana%20de%20Parnaiba%20Sao%20Paulo%20Brazil%2C%20premium%20residential%20condominium%20exterior%2C%20elegant%20stone%20and%20concrete%20facade%2C%20lush%20tropical%20landscaping%2C%20private%20garden%2C%20warm%20afternoon%20sunlight%2C%20sophisticated%20quiet%20luxury%20real%20estate%20photography%2C%20neutral%20tones&width=800&height=520&seq=portfolio10res&orientation=landscape',
  11: 'https://readdy.ai/api/search-image?query=high%20end%20residential%20house%20Aldeia%20da%20Serra%20Barueri%20Sao%20Paulo%20Brazil%2C%20exclusive%20gated%20condominium%20premium%20architecture%2C%20large%20garden%20with%20mature%20trees%2C%20natural%20wood%20and%20stone%20materials%2C%20floor%20to%20ceiling%20windows%2C%20refined%20minimalist%20design%2C%20warm%20golden%20light%20real%20estate%20photography&width=800&height=520&seq=portfolio11res&orientation=landscape',
  12: 'https://readdy.ai/api/search-image?query=luxury%20apartment%20interior%20living%20room%20Moema%20Sao%20Paulo%20Brazil%2C%20upscale%20residential%20building%2C%20sophisticated%20living%20room%20premium%20furniture%2C%20marble%20floors%2C%20large%20balcony%20with%20green%20city%20view%2C%20neutral%20warm%20tones%2C%20high%20end%20finishes%2C%20refined%20residential%20photography%20quiet%20luxury&width=800&height=520&seq=portfolio12res&orientation=landscape',
  13: 'https://readdy.ai/api/search-image?query=penthouse%20terrace%20viewed%20from%20outside%20looking%20inward%20through%20wide%20open%20frameless%20sliding%20glass%20doors%2C%20luxury%20Sao%20Paulo%20Brazil%20high%20rise%20apartment%2C%20stone%20paver%20outdoor%20terrace%20floor%20in%20foreground%2C%20minimalist%20steel%20railing%20at%20terrace%20edge%2C%20open%20sky%20above%20no%20glass%20roof%20no%20overhead%20structure%2C%20interior%20reveals%20sophisticated%20living%20room%20with%20large%20format%20cream%20porcelain%20floor%2C%20long%20low%20contemporary%20sofa%20in%20warm%20sand%20fabric%20facing%20left%2C%20natural%20oak%20wood%20feature%20wall%20panel%2C%20integrated%20LED%20cove%20ceiling%20lighting%2C%20single%20sculptural%20floor%20lamp%2C%20one%20tall%20fiddle%20leaf%20fig%20in%20matte%20black%20planter%20on%20terrace%20right%20side%2C%20Sao%20Paulo%20urban%20skyline%20at%20golden%20dusk%20glowing%20amber%20and%20warm%20orange%20in%20background%2C%20sense%20of%20height%20exclusivity%20and%20patrimony%2C%20quiet%20luxury%20aesthetic%2C%20neutral%20palette%20beige%20warm%20gray%20natural%20wood%2C%20no%20people%2C%20no%20watermark%2C%20no%20text%2C%20no%20logo%2C%20architectural%20digest%20editorial%20real%20estate%20photography%20ultra%20premium&width=800&height=520&seq=jardim-europa-authored-dusk-terrace-v4&orientation=landscape',
  14: 'https://public.readdy.ai/ai/img_res/edited_ed9468ec52c5f22ba1f3a6df95410351_ffbc4b42.jpg',
  15: 'https://readdy.ai/api/search-image?query=high%20standard%20luxury%20apartment%20building%20Vila%20Nova%20Conceicao%20Sao%20Paulo%20Brazil%2C%20upscale%20residential%20facade%2C%20elegant%20modern%20architecture%20with%20clean%20lines%2C%20refined%20lobby%20entrance%20with%20marble%2C%20premium%20finishes%2C%20exclusive%20condominium%20exterior%2C%20soft%20daytime%20lighting%2C%20quiet%20luxury%20real%20estate&width=800&height=520&seq=portfolio15res&orientation=landscape',
  16: 'https://public.readdy.ai/ai/img_res/edited_85a680932b2d3cca9e1f5b4a98cc1f7d_f978f475.jpg',
  17: 'https://readdy.ai/api/search-image?query=ultra%20luxury%20oceanfront%20apartment%20balcony%20Balneario%20Camboriu%20Santa%20Catarina%20Brazil%2C%20high%20rise%20premium%20tower%20direct%20sea%20view%2C%20large%20terrace%20with%20ocean%20panorama%2C%20modern%20glass%20architecture%2C%20turquoise%20water%20and%20city%20in%20background%2C%20exclusive%20real%20estate%20photography%2C%20refined%20quiet%20luxury&width=800&height=520&seq=portfolio17res&orientation=landscape',
  20: 'https://readdy.ai/api/search-image?query=premium%20medical%20consulting%20room%20interior%20Sao%20Paulo%20Brazil%2C%20elegant%20clinical%20office%20space%20with%20neutral%20warm%20tones%2C%20marble%20reception%20desk%2C%20refined%20medical%20furniture%2C%20soft%20professional%20lighting%2C%20sophisticated%20and%20clean%20minimalist%20design%2C%20premium%20healthcare%20environment&width=800&height=520&seq=portfolio20corp&orientation=landscape',
  21: 'https://readdy.ai/api/search-image?query=high%20end%20boutique%20retail%20store%20interior%20Moema%20Sao%20Paulo%20Brazil%2C%20luxury%20commercial%20space%20with%20polished%20concrete%20floors%2C%20elegant%20display%20shelving%20with%20warm%20lighting%2C%20sophisticated%20minimal%20decor%2C%20premium%20commercial%20real%20estate%20interior%2C%20quiet%20luxury%20retail%20atmosphere&width=800&height=520&seq=portfolio21corp&orientation=landscape',
  22: 'https://readdy.ai/api/search-image?query=modern%20premium%20corporate%20office%20suite%20interior%20Avenida%20Chucri%20Zaidan%20Sao%20Paulo%20Brazil%2C%20executive%20workspace%20with%20city%20skyline%20view%2C%20floor%20to%20ceiling%20glass%20windows%2C%20warm%20neutral%20tones%2C%20sophisticated%20open%20plan%20layout%2C%20refined%20corporate%20environment%2C%20professional%20natural%20lighting&width=800&height=520&seq=portfolio22corp&orientation=landscape',
  23: 'https://readdy.ai/api/search-image?query=upscale%20commercial%20ensemble%20interior%20Itaim%20Bibi%20Sao%20Paulo%20Brazil%2C%20premium%20corporate%20space%20with%20double%20height%20lobby%2C%20marble%20floors%2C%20elegant%20reception%20area%2C%20sophisticated%20business%20environment%2C%20natural%20stone%20and%20glass%20architecture%2C%20refined%20professional%20atmosphere&width=800&height=520&seq=portfolio23corp&orientation=landscape',
  24: 'https://readdy.ai/api/search-image?query=elegant%20corporate%20office%20suite%20Brooklin%20Sao%20Paulo%20Brazil%2C%20premium%20executive%20workspace%2C%20minimalist%20design%20with%20warm%20wood%20accents%2C%20large%20windows%20with%20city%20view%2C%20sophisticated%20meeting%20area%2C%20refined%20neutral%20palette%2C%20professional%20business%20environment%20quiet%20luxury&width=800&height=520&seq=portfolio24corp&orientation=landscape',
  25: 'https://readdy.ai/api/search-image?query=luxury%20premium%20professional%20consulting%20office%20Moema%20Passaros%20Sao%20Paulo%20Brazil%2C%20upscale%20clinical%20or%20business%20space%20interior%2C%20sophisticated%20decor%20with%20marble%20and%20wood%20accents%2C%20elegant%20private%20office%2C%20warm%20professional%20lighting%2C%20refined%20quiet%20luxury%20atmosphere&width=800&height=520&seq=portfolio25corp&orientation=landscape',
  30: 'https://readdy.ai/api/search-image?query=luxury%20jet%20ski%20personal%20watercraft%20on%20turquoise%20clear%20water%2C%20high%20performance%20watercraft%20aspirational%20lifestyle%20photography%2C%20beautiful%20ocean%20or%20lake%20setting%2C%20premium%20watercraft%20design%2C%20bright%20sunny%20day%2C%20sophisticated%20aquatic%20leisure%2C%20vibrant%20water%20sports%20lifestyle&width=800&height=520&seq=portfolio30lifestyle&orientation=landscape',
  40: 'https://readdy.ai/api/search-image?query=luxury%20gated%20condominium%20house%20Jardim%20Marajoara%20south%20zone%20Sao%20Paulo%20Brazil%2C%20premium%20residential%20facade%20with%20elegant%20stone%20and%20glass%20architecture%2C%20manicured%20private%20garden%20with%20tropical%20plants%2C%20modern%20minimalist%20design%2C%20sophisticated%20quiet%20luxury%20family%20home%20exterior%2C%20refined%20architectural%20details%2C%20lush%20greenery%2C%20warm%20golden%20afternoon%20light%2C%20real%20estate%20photography&width=800&height=520&seq=portfolio40resmar&orientation=landscape',
  41: 'https://readdy.ai/api/search-image?query=high%20end%20urban%20residence%20Vila%20Sofia%20south%20zone%20Sao%20Paulo%20Brazil%2C%20sophisticated%20single%20family%20home%20exterior%20premium%20architecture%2C%20large%20windows%20elegant%20proportions%2C%20well%20maintained%20garden%20mature%20trees%2C%20warm%20stone%20and%20wood%20facade%20materials%2C%20refined%20family%20residential%20photography%20quiet%20luxury%2C%20warm%20soft%20light%20elegant%20exclusive%20home&width=800&height=520&seq=portfolio41resvs&orientation=landscape',
  42: 'https://readdy.ai/api/search-image?query=luxury%20gated%20community%20condominium%20house%20Vila%20Sofia%20Sao%20Paulo%20Brazil%2C%20premium%20residential%20entrance%20sophisticated%20architecture%2C%20elegant%20landscaped%20garden%20tropical%20plants%2C%20clean%20modern%20lines%20natural%20stone%20materials%2C%20private%20secure%20condominium%20setting%2C%20warm%20golden%20afternoon%20light%20quiet%20luxury%20family%20home%2C%20refined%20residential%20real%20estate%20photography&width=800&height=520&seq=portfolio42resvsco&orientation=landscape',
  43: 'https://readdy.ai/api/search-image?query=elegant%20premium%20family%20house%20Jardim%20Marajoara%20south%20Sao%20Paulo%20Brazil%2C%20high%20standard%20residential%20architecture%20generous%20proportions%2C%20large%20windows%20floor%20to%20ceiling%2C%20beautiful%20landscaped%20garden%20tall%20tropical%20trees%2C%20premium%20natural%20stone%20warm%20wood%20materials%2C%20welcoming%20family%20atmosphere%20sophisticated%20design%2C%20warm%20golden%20afternoon%20light%20exterior%20quiet%20luxury&width=800&height=520&seq=portfolio43resfam&orientation=landscape',
  44: 'https://readdy.ai/api/search-image?query=exclusive%20gated%20condominium%20house%20Alto%20da%20Boa%20Vista%20Sao%20Paulo%20Brazil%2C%20premium%20residential%20complex%20surrounded%20lush%20Atlantic%20forest%20vegetation%2C%20elegant%20sophisticated%20architecture%20natural%20stone%20and%20wood%2C%20private%20garden%20mature%20trees%20soft%20landscaping%2C%20hillside%20serene%20exclusive%20setting%2C%20quiet%20luxury%20residential%20photography%20refined%20elegant%20exterior%20warm%20light&width=800&height=520&seq=portfolio44resabv&orientation=landscape',
};

const getProjectImage = (id: number): string =>
  IMAGE_MAP[id] ?? IMAGE_MAP[4];

export default function PortfolioSection() {
  const { t } = useTranslation('home');
  const [activeCategory, setActiveCategory] = useState('highlights');
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [visibleProjects, setVisibleProjects] = useState(3);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const carouselRef = useRef<HTMLDivElement>(null);

  const projects = t('portfolio.projects', { returnObjects: true }) as Project[];
  const categories = t('portfolio.categories', { returnObjects: true }) as Record<string, string>;
  const secondaryNote = t('portfolio.secondaryNote');

  const featuredProjects = projects.filter((p) => p.featured === true);
  const cardsPerView = 3;

  const filteredProjects =
    activeCategory === 'highlights'
      ? featuredProjects
      : projects.filter((p) => p.category === activeCategory);

  // Carousel is used for both 'highlights' and 'residential'
  const isCarouselCategory = activeCategory === 'highlights' || activeCategory === 'residential';
  const carouselProjects = activeCategory === 'highlights' ? featuredProjects : filteredProjects;
  const maxCarouselIndex = Math.max(0, carouselProjects.length - cardsPerView);

  const prevSlide = () => setCarouselIndex((i) => Math.max(0, i - 1));
  const nextSlide = () => setCarouselIndex((i) => Math.min(maxCarouselIndex, i + 1));

  const handleCategoryChange = (key: string) => {
    setActiveCategory(key);
    setVisibleProjects(3);
    setCarouselIndex(0);
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-slide-up');
          }
        });
      },
      { threshold: 0.1 },
    );
    document.querySelectorAll('.portfolio-item').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [filteredProjects, visibleProjects]);

  const openWhatsApp = (project: Project) => {
    const msg = `Olá! Tenho interesse no ativo ${project.title} (${project.value}). Gostaria de solicitar uma análise patrimonial.`;
    window.open(`https://wa.me/5511930322408?text=${encodeURIComponent(msg)}`, '_blank');
  };

  return (
    <>
      <section className="py-24 bg-gradient-to-br from-stone-50 to-amber-25">
        <div className="container mx-auto px-6">
          {/* Header */}
          <div className="text-center mb-16">
            <div className="inline-block bg-amber-100 text-amber-800 px-4 py-2 rounded-full text-sm font-medium mb-4 tracking-widest uppercase">
              {t('portfolio.eyebrow')}
            </div>
            <h2 className="text-4xl lg:text-5xl font-light text-stone-900 mb-6 font-serif">
              {t('portfolio.title')}
            </h2>
            <p className="text-xl text-stone-600 max-w-3xl mx-auto leading-relaxed">
              {t('portfolio.subtitle')}
            </p>
          </div>

          {/* Category Filter */}
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            {Object.entries(categories).map(([key, label]) => (
              <button
                key={key}
                onClick={() => handleCategoryChange(key)}
                className={`px-6 py-3 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
                  activeCategory === key
                    ? 'bg-stone-900 text-white transform scale-105'
                    : 'bg-white text-stone-600 border border-stone-200 hover:border-stone-300 hover:scale-105'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {/* ── DESTAQUES / RESIDENCIAL — Carousel ── */}
          {isCarouselCategory && (
            <div>
              <div className="relative">
                {/* Carousel viewport */}
                <div className="overflow-hidden" ref={carouselRef}>
                  <div
                    className="flex transition-transform duration-500 ease-out"
                    style={{ transform: `translateX(-${carouselIndex * (100 / cardsPerView)}%)` }}
                  >
                    {carouselProjects.map((project) => (
                      <div
                        key={project.id}
                        className="w-1/3 flex-shrink-0 px-4"
                        style={{ minWidth: '33.333%' }}
                      >
                        <div
                          className="group cursor-pointer bg-white rounded-2xl overflow-hidden hover:-translate-y-2 transition-all duration-500"
                          onClick={() => setSelectedProject(project)}
                        >
                          <div className="relative h-64 overflow-hidden">
                            <img
                              src={getProjectImage(project.id)}
                              alt={project.title}
                              className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-110"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                            <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full">
                              <span className="text-sm font-semibold text-stone-900">{project.value}</span>
                            </div>
                            {activeCategory === 'highlights' && (
                              <div className="absolute top-4 left-4">
                                <span className={`px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider ${
                                  project.category === 'automotive'
                                    ? 'bg-stone-900/80 text-amber-300 backdrop-blur-sm'
                                    : 'bg-amber-500/90 text-white backdrop-blur-sm'
                                }`}>
                                  {project.category === 'automotive' ? 'Automotivo' : 'Residencial'}
                                </span>
                              </div>
                            )}
                            <div className="absolute bottom-4 left-4 opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-4 group-hover:translate-y-0">
                              <button className="bg-white text-stone-900 px-4 py-2 rounded-full text-sm font-medium hover:bg-amber-100 transition-colors whitespace-nowrap cursor-pointer">
                                Ver Detalhes
                              </button>
                            </div>
                          </div>
                          <div className="p-6">
                            <div className="flex flex-wrap gap-2 mb-3">
                              {project.tags.slice(0, 2).map((tag) => (
                                <span key={tag} className="bg-amber-50 text-amber-700 px-3 py-1 rounded-full text-xs font-medium">
                                  {tag}
                                </span>
                              ))}
                            </div>
                            <h3 className="text-xl font-semibold text-stone-900 mb-2 group-hover:text-amber-700 transition-colors">
                              {project.title}
                            </h3>
                            <p className="text-stone-500 text-sm mb-3 flex items-center">
                              <i className="ri-map-pin-line mr-2"></i>
                              {project.location}
                            </p>
                            <p className="text-stone-600 text-sm leading-relaxed line-clamp-2">
                              {project.description}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Navigation arrows */}
                <button
                  onClick={prevSlide}
                  disabled={carouselIndex === 0}
                  className={`absolute -left-5 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-white border border-stone-200 rounded-full transition-all duration-200 cursor-pointer z-10 ${
                    carouselIndex === 0 ? 'opacity-30 cursor-not-allowed' : 'hover:bg-stone-50 hover:border-stone-400'
                  }`}
                >
                  <i className="ri-arrow-left-s-line text-stone-700 text-lg"></i>
                </button>
                <button
                  onClick={nextSlide}
                  disabled={carouselIndex >= maxCarouselIndex}
                  className={`absolute -right-5 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-white border border-stone-200 rounded-full transition-all duration-200 cursor-pointer z-10 ${
                    carouselIndex >= maxCarouselIndex ? 'opacity-30 cursor-not-allowed' : 'hover:bg-stone-50 hover:border-stone-400'
                  }`}
                >
                  <i className="ri-arrow-right-s-line text-stone-700 text-lg"></i>
                </button>
              </div>

              {/* Carousel dots */}
              <div className="flex justify-center gap-2 mt-8">
                {Array.from({ length: maxCarouselIndex + 1 }).map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setCarouselIndex(i)}
                    className={`rounded-full transition-all duration-300 cursor-pointer ${
                      i === carouselIndex ? 'w-6 h-2 bg-stone-800' : 'w-2 h-2 bg-stone-300'
                    }`}
                  />
                ))}
              </div>

              {/* Secondary note — only on Destaques */}
              {activeCategory === 'highlights' && (
                <div className="mt-16 border-t border-stone-200 pt-10 text-center max-w-2xl mx-auto">
                  <p className="text-sm text-stone-500 leading-relaxed tracking-wide">
                    {secondaryNote}
                  </p>
                  <div className="flex justify-center gap-6 mt-6">
                    {(['corporate', 'lifestyle'] as const).map((cat) => (
                      <button
                        key={cat}
                        onClick={() => handleCategoryChange(cat)}
                        className="text-xs text-stone-400 hover:text-stone-700 border border-stone-200 hover:border-stone-400 px-4 py-2 rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer capitalize"
                      >
                        {categories[cat]}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── AUTOMOTIVO — Specialized layout ── */}
          {activeCategory === 'automotive' && <AutomotiveShowcase />}

          {/* ── OTHER CATEGORIES (corporate, lifestyle…) — Grid ── */}
          {!isCarouselCategory && activeCategory !== 'automotive' && (
            <>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
                {filteredProjects.slice(0, visibleProjects).map((project, index) => (
                  <div
                    key={project.id}
                    className="portfolio-item opacity-0 group cursor-pointer"
                    style={{ animationDelay: `${index * 0.1}s` }}
                    onClick={() => setSelectedProject(project)}
                  >
                    <div className="bg-white rounded-2xl overflow-hidden hover:-translate-y-2 transition-all duration-500">
                      <div className="relative h-56 overflow-hidden">
                        <img
                          src={getProjectImage(project.id)}
                          alt={project.title}
                          className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-110"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                        <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full">
                          <span className="text-sm font-semibold text-stone-900">{project.value}</span>
                        </div>
                        <div className="absolute bottom-4 left-4 opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-4 group-hover:translate-y-0">
                          <button className="bg-white text-stone-900 px-4 py-2 rounded-full text-sm font-medium hover:bg-amber-100 transition-colors whitespace-nowrap cursor-pointer">
                            Ver Detalhes
                          </button>
                        </div>
                      </div>
                      <div className="p-6">
                        <div className="flex flex-wrap gap-2 mb-3">
                          {project.tags.slice(0, 2).map((tag) => (
                            <span key={tag} className="bg-amber-50 text-amber-700 px-3 py-1 rounded-full text-xs font-medium">
                              {tag}
                            </span>
                          ))}
                        </div>
                        <h3 className="text-xl font-semibold text-stone-900 mb-2 group-hover:text-amber-700 transition-colors">
                          {project.title}
                        </h3>
                        <p className="text-stone-500 text-sm mb-3 flex items-center">
                          <i className="ri-map-pin-line mr-2"></i>
                          {project.location}
                        </p>
                        <p className="text-stone-600 text-sm leading-relaxed">
                          {project.description}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {visibleProjects < filteredProjects.length && (
                <div className="text-center">
                  <button
                    onClick={() => setVisibleProjects((prev) => prev + 3)}
                    className="bg-stone-900 text-white px-8 py-4 rounded-full font-medium hover:bg-stone-800 transition-all duration-300 transform hover:scale-105 whitespace-nowrap cursor-pointer"
                  >
                    Carregar Mais
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* ── Modal — Premium Presentation ── */}
      {selectedProject && (
        <div
          className="fixed inset-0 bg-black/75 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedProject(null)}
        >
          <div
            className="bg-white rounded-3xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Image header */}
            <div className="relative h-80 overflow-hidden rounded-t-3xl flex-shrink-0">
              <img
                src={getProjectImage(selectedProject.id)}
                alt={selectedProject.title}
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/10 to-transparent" />
              <button
                onClick={() => setSelectedProject(null)}
                className="absolute top-5 right-5 w-9 h-9 flex items-center justify-center bg-white/20 backdrop-blur-sm text-white rounded-full hover:bg-white/35 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
              <div className="absolute bottom-6 left-7 text-white">
                <p className="text-amber-300 text-xs font-semibold tracking-widest uppercase mb-2 flex items-center gap-2">
                  <i className="ri-map-pin-2-line"></i>
                  {selectedProject.location}
                </p>
                <h3 className="text-3xl font-semibold">{selectedProject.title}</h3>
              </div>
            </div>

            {/* Body */}
            <div className="p-8 lg:p-10">
              <div className="grid lg:grid-cols-2 gap-10">
                {/* Left — info */}
                <div>
                  <p className="text-stone-600 leading-relaxed mb-7 text-base">
                    {selectedProject.description}
                  </p>

                  {/* Details */}
                  <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mb-7">
                    {selectedProject.details.area && (
                      <div className="text-center p-4 bg-stone-50 rounded-xl">
                        <div className="w-8 h-8 flex items-center justify-center mx-auto mb-2">
                          <i className="ri-ruler-line text-xl text-amber-600"></i>
                        </div>
                        <div className="font-semibold text-stone-900 text-sm">{selectedProject.details.area}</div>
                        <div className="text-xs text-stone-500 mt-0.5">Área</div>
                      </div>
                    )}
                    {selectedProject.details.vehicleType && (
                      <div className="text-center p-4 bg-stone-50 rounded-xl">
                        <div className="w-8 h-8 flex items-center justify-center mx-auto mb-2">
                          <i className="ri-car-line text-xl text-amber-600"></i>
                        </div>
                        <div className="font-semibold text-stone-900 text-sm">{selectedProject.details.vehicleType}</div>
                        <div className="text-xs text-stone-500 mt-0.5">Tipo</div>
                      </div>
                    )}
                    <div className="text-center p-4 bg-stone-50 rounded-xl">
                      <div className="w-8 h-8 flex items-center justify-center mx-auto mb-2">
                        <i className="ri-building-line text-xl text-amber-600"></i>
                      </div>
                      <div className="font-semibold text-stone-900 text-sm">{selectedProject.details.type}</div>
                      <div className="text-xs text-stone-500 mt-0.5">Tipologia</div>
                    </div>
                    {selectedProject.details.bedrooms && (
                      <div className="text-center p-4 bg-stone-50 rounded-xl">
                        <div className="w-8 h-8 flex items-center justify-center mx-auto mb-2">
                          <i className="ri-hotel-bed-line text-xl text-amber-600"></i>
                        </div>
                        <div className="font-semibold text-stone-900 text-sm">{selectedProject.details.bedrooms}</div>
                        <div className="text-xs text-stone-500 mt-0.5">Quartos</div>
                      </div>
                    )}
                    {selectedProject.details.bathrooms && (
                      <div className="text-center p-4 bg-stone-50 rounded-xl">
                        <div className="w-8 h-8 flex items-center justify-center mx-auto mb-2">
                          <i className="ri-drop-line text-xl text-amber-600"></i>
                        </div>
                        <div className="font-semibold text-stone-900 text-sm">{selectedProject.details.bathrooms}</div>
                        <div className="text-xs text-stone-500 mt-0.5">Banheiros</div>
                      </div>
                    )}
                    {selectedProject.details.parking && (
                      <div className="text-center p-4 bg-stone-50 rounded-xl">
                        <div className="w-8 h-8 flex items-center justify-center mx-auto mb-2">
                          <i className="ri-parking-box-line text-xl text-amber-600"></i>
                        </div>
                        <div className="font-semibold text-stone-900 text-sm">{selectedProject.details.parking}</div>
                        <div className="text-xs text-stone-500 mt-0.5">Garagem</div>
                      </div>
                    )}
                    {selectedProject.details.completion && (
                      <div className="text-center p-4 bg-stone-50 rounded-xl">
                        <div className="w-8 h-8 flex items-center justify-center mx-auto mb-2">
                          <i className="ri-time-line text-xl text-amber-600"></i>
                        </div>
                        <div className="font-semibold text-stone-900 text-sm">{selectedProject.details.completion}</div>
                        <div className="text-xs text-stone-500 mt-0.5">Prazo médio</div>
                      </div>
                    )}
                  </div>

                  {/* Tags */}
                  <div className="flex flex-wrap gap-2">
                    {selectedProject.tags.map((tag) => (
                      <span key={tag} className="bg-amber-100 text-amber-700 px-4 py-1.5 rounded-full text-xs font-medium">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Right — Premium credit panel */}
                <div className="bg-stone-900 rounded-2xl p-8 flex flex-col justify-between">
                  <div>
                    <p className="text-amber-400 text-xs font-semibold tracking-widest uppercase mb-6">
                      Referência de Crédito
                    </p>

                    {selectedProject.creditRef && (
                      <div className="mb-5 pb-5 border-b border-stone-700">
                        <p className="text-stone-400 text-xs uppercase tracking-widest mb-1">Crédito</p>
                        <p className="text-white text-2xl font-light">{selectedProject.creditRef}</p>
                      </div>
                    )}

                    {selectedProject.parcRef && (
                      <div className="mb-5 pb-5 border-b border-stone-700">
                        <p className="text-stone-400 text-xs uppercase tracking-widest mb-1">Parcela Ref.</p>
                        <p className="text-amber-300 text-2xl font-light">{selectedProject.parcRef}</p>
                      </div>
                    )}

                    {selectedProject.prazoRef && (
                      <div className={`${(selectedProject.condominioRef || selectedProject.iptuRef) ? 'mb-5 pb-5 border-b border-stone-700' : 'mb-8'}`}>
                        <p className="text-stone-400 text-xs uppercase tracking-widest mb-1">Prazo</p>
                        <p className="text-white text-2xl font-light">{selectedProject.prazoRef}</p>
                      </div>
                    )}

                    {selectedProject.condominioRef && (
                      <div className={`${selectedProject.iptuRef ? 'mb-5 pb-5 border-b border-stone-700' : 'mb-8'}`}>
                        <p className="text-stone-400 text-xs uppercase tracking-widest mb-1">Condomínio</p>
                        <p className="text-white text-xl font-light">{selectedProject.condominioRef}</p>
                      </div>
                    )}

                    {selectedProject.iptuRef && (
                      <div className="mb-8">
                        <p className="text-stone-400 text-xs uppercase tracking-widest mb-1">IPTU</p>
                        <p className="text-white text-xl font-light">{selectedProject.iptuRef}</p>
                      </div>
                    )}

                    <p className="text-stone-500 text-xs leading-relaxed mb-8">
                      Valores referenciais sujeitos à análise patrimonial individual. Condições específicas dependem do perfil, regulamento da administradora e composição do grupo.
                    </p>
                  </div>

                  <button
                    onClick={() => openWhatsApp(selectedProject)}
                    className="w-full bg-amber-500 hover:bg-amber-400 text-stone-900 font-semibold py-4 rounded-xl transition-colors duration-200 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-message-2-line text-lg"></i>
                    Solicitar análise
                  </button>
                </div>
              </div>

              <div className="text-center mt-8">
                <button
                  onClick={() => setSelectedProject(null)}
                  className="text-stone-400 hover:text-stone-700 text-sm border border-stone-200 hover:border-stone-400 px-6 py-2.5 rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes slide-up {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-slide-up { animation: slide-up 0.6s ease-out forwards; }
        .line-clamp-2 { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
      `}</style>
    </>
  );
}
